import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Contract facts — confirmed contract-level terms of an engagement (vendor,
 * value, dates, retainage, warranty, ...). Read from the contract by AI and
 * confirmed in the review queue, or typed by a person.
 *
 *   GET /api/v1/contract/engagements/:id/facts
 *   PUT /api/v1/contract/engagements/:id/facts/:fact_key   { value | null }
 */
export const ContractFactSchema = z.object({
  fact_key: z.string(),
  label: z.string(),
  kind: z.enum(['text', 'date']),
  value: z.string().nullable(),
  source: z.enum(['ai_confirmed', 'manual']).nullable(),
  document_id: z.string().nullable(),
  document_title: z.string().nullable(),
  page_number: z.number().nullable(),
  updated_at: z.string().nullable(),
});
export type ContractFact = z.infer<typeof ContractFactSchema>;

const ContractFactsSchema = z.object({
  facts: z.array(ContractFactSchema),
  pending_proposals: z.number(),
});
export type ContractFacts = z.infer<typeof ContractFactsSchema>;

const PutResultSchema = z.object({ fact_key: z.string(), value: z.string().nullable() });

const path = (engagementId: string) =>
  `/api/v1/contract/engagements/${encodeURIComponent(engagementId)}/facts`;

// Key shape `[namespace, engagementId]` keeps it inside the engagement
// live-refresh invalidation predicate.
export function useContractFactsQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['contract-facts', engagementId] as const,
    queryFn: () => apiClient(path(engagementId!), ContractFactsSchema),
    enabled: Boolean(engagementId),
  });
}

/** Convenience lookup: the confirmed value of one fact, or null. */
export function factValue(facts: ContractFacts | undefined, key: string): string | null {
  return facts?.facts.find((f) => f.fact_key === key)?.value ?? null;
}

export function useSetContractFactMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ factKey, value }: { factKey: string; value: string | null }) =>
      apiClient(`${path(engagementId)}/${encodeURIComponent(factKey)}`, PutResultSchema, {
        method: 'PUT',
        body: { value },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-facts', engagementId] });
    },
  });
}
